import type { SvgIconComponent } from '@mui/icons-material';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import type { Theme } from '@mui/material';
import {
  DashboardPersonaBodyGoalEnum as DashboardGoal,
  DashboardPersonaBodyPersonaEnum as DashboardPersona,
} from 'src/openapi';

export { DashboardGoal, DashboardPersona };

type PersonaTone = 'gold' | 'blue' | 'navy';

interface PersonaMeta {
  label: string;
  Icon: SvgIconComponent;
  tone: PersonaTone;
  /** What the dashboard leads with, for the Settings menu. */
  summary: string;
  /** What this dashboard leads with, revealed one by one on the building
   *  screen. Things the dashboard shows, not work being done: nothing runs
   *  while they appear. */
  highlights: string[];
}

export const PERSONA_META: Record<DashboardPersona, PersonaMeta> = {
  [DashboardPersona.Learner]: {
    label: 'Learner',
    Icon: MenuBookRoundedIcon,
    tone: 'gold',
    summary: 'Courses, daily missions and skills first',
    highlights: [
      'Courses picked for your skills',
      'Daily missions for your streak',
      'Your skill levels',
    ],
  },
  [DashboardPersona.JobFinder]: {
    label: 'Job finder',
    Icon: WorkOutlineRoundedIcon,
    tone: 'blue',
    summary: 'Jobs matched to your resume first',
    highlights: [
      'Jobs matched to your resume',
      'Your resume and its score',
      'Tools to tailor each application',
    ],
  },
  [DashboardPersona.Experienced]: {
    label: 'Experienced',
    Icon: RocketLaunchRoundedIcon,
    tone: 'navy',
    summary: 'Roles at your level and applications first',
    highlights: [
      'Roles matched to your experience',
      'Your applications at a glance',
      'Courses picked for your skills',
    ],
  },
};

const GOAL_PHRASE: Record<DashboardGoal, string> = {
  [DashboardGoal.BuildSkills]: 'build your skills',
  [DashboardGoal.GetJob]: 'get a job',
  [DashboardGoal.GrowCareer]: 'move up in your career',
};

/** Why this dashboard, in one sentence, from what the server decided on. */
export const personaReason = (
  persona: DashboardPersona,
  goal?: string | null,
  experienceYears?: number | null,
) => {
  if (persona === DashboardPersona.Experienced) {
    const years = experienceYears
      ? `${Math.round(experienceYears)} years of experience`
      : 'your experience';
    return `Your resume shows ${years}, so we've put roles at your level and your applications first.`;
  }
  const want = goal
    ? `You want to ${GOAL_PHRASE[goal as DashboardGoal]}. `
    : '';
  return persona === DashboardPersona.JobFinder
    ? `${want}Your home now leads with matched jobs, your applications and placement prep.`
    : `${want}Your home now leads with your courses, daily practice and skill levels.`;
};

export const isDashboardPersona = (value: unknown): value is DashboardPersona =>
  Object.values(DashboardPersona).includes(value as DashboardPersona);

/** The tile colours each persona is drawn in, read off the current theme. */
export const personaTone = (theme: Theme, persona: DashboardPersona) => {
  const gc = theme.palette.guidedCourse;
  return {
    gold: { bg: gc.gold, fg: gc.recommendedColor, shadow: gc.goldDeep },
    blue: { bg: gc.blue, fg: '#fff', shadow: gc.blueDeep },
    navy: {
      bg: gc.blueInk,
      fg: gc.gold,
      shadow: theme.darken(gc.blueInk, 0.5),
    },
  }[PERSONA_META[persona].tone];
};

/** The persona to draw the dashboard as. Unanswered reads as Learner, the
 * dashboard everyone had before personas existed. */
export const resolvePersona = (persona?: string | null): DashboardPersona =>
  isDashboardPersona(persona) ? persona : DashboardPersona.Learner;

export type DashboardSectionKey =
  | 'classInvites'
  | 'hero'
  | 'mobileApp'
  | 'missions'
  | 'careerCall'
  | 'courses'
  | 'competitions'
  | 'multiplayer'
  | 'skills'
  | 'resume'
  | 'jobs'
  | 'applications'
  | 'applySmarter'
  | 'hiddenJobs'
  // Not in the design, in the same card style.
  | 'community'
  | 'internships'
  | 'needsAttention'
  | 'preferences'
  | 'earnCredits'
  | 'referAndEarn'
  | 'promoCards';

/** Everything the design leaves out, in the order it trails every dashboard. */
const TRAILING_SECTIONS: DashboardSectionKey[] = [
  'needsAttention',
  'preferences',
  'earnCredits',
  'referAndEarn',
  'promoCards',
];

/**
 * The design's section order per persona. Class invites stay first everywhere —
 * they come from a teacher and expire. Experienced has no jobs feed: its hero
 * is already the list of roles at its level. The design has no career call for
 * Experienced; it is kept, paired with missions, so the offer still reaches it.
 */
export const DASHBOARD_SECTION_ORDER: Record<
  DashboardPersona,
  DashboardSectionKey[]
> = {
  [DashboardPersona.Learner]: [
    'classInvites',
    'hero',
    'missions',
    'careerCall',
    'courses',
    'multiplayer',
    'competitions',
    'mobileApp',
    'skills',
    'jobs',
    'resume',
    'community',
    'applications',
    'internships',
    ...TRAILING_SECTIONS,
  ],
  [DashboardPersona.JobFinder]: [
    'classInvites',
    'hero',
    'jobs',
    'applySmarter',
    'hiddenJobs',
    'resume',
    'community',
    'skills',
    'competitions',
    'mobileApp',
    'courses',
    'multiplayer',
    'careerCall',
    'missions',
    ...TRAILING_SECTIONS,
  ],
  [DashboardPersona.Experienced]: [
    'classInvites',
    'hero',
    'applications',
    'courses',
    'multiplayer',
    'competitions',
    'mobileApp',
    'resume',
    'community',
    'skills',
    'missions',
    'careerCall',
    ...TRAILING_SECTIONS,
  ],
};

export type SectionWidth = 'full' | 'half' | 'wide' | 'narrow';

/**
 * How much of a row a section takes on a wide screen. Pairs sit next to each
 * other in every order above (missions + call, resume builder + community);
 * when one of a pair renders nothing, or a half has no partner, it grows into
 * the whole row rather than leaving a hole.
 */
export const DASHBOARD_SECTION_WIDTH: Partial<
  Record<DashboardSectionKey, SectionWidth>
> = {
  missions: 'wide',
  careerCall: 'narrow',
  skills: 'half',
  resume: 'half',
  community: 'half',
};

export const PERSONA_GREETING: Record<DashboardPersona, string> = {
  [DashboardPersona.Learner]: 'Keep your streak going.',
  [DashboardPersona.JobFinder]: "Here's your job hunt.",
  [DashboardPersona.Experienced]: "Here's what's new at your level.",
};
